import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData.js';
import imageIdToURI from '../../utilities/imageIdToURI.js';
import VoxelManager from '../../utilities/VoxelManager.js';
import CompositeVoxelManager from '../../utilities/CompositeVoxelManager.js';
import volumeTextureStore from '../volumeTextureStore.js';
import { provideReducedImages } from '../reducedVolumeImages.js';
import { isMutableSlab } from './VolumeTextureSet.js';
import { boundsOfFrame, normalizedFactors, voxelGridKey, voxelGridsEqual, } from '../../utilities/voxelGrid/index.js';
import { vtkStreamingOpenGLTexture } from '../../RenderingEngine/vtkClasses/index.js';
import ImageQualityStatus from '../../enums/ImageQualityStatus.js';
import VoxelStatistics from '../../enums/VoxelStatistics.js';
import cache from '../cache.js';
import { coreLog } from '../../utilities/logger.js';
const log = coreLog.getLogger('cache', 'ImageVolume');
export const FULL_RESOLUTION_TEXTURE_SET = 'full-resolution/full-extent';
function markSlice(slot, slice) {
    const texture = isMutableSlab(slot) ? slot.writeTarget() : slot.texture;
    texture.setUpdatedFrame(slice);
}
export class ImageVolume {
    get numTimePoints() {
        return typeof this.numDimensionGroups === 'number'
            ? this.numDimensionGroups
            : 1;
    }
    constructor(props) {
        this._imageIdsIndexMap = new Map();
        this._imageURIsIndexMap = new Map();
        this.cornerstoneImageMetaData = null;
        this.isPreScaled = false;
        this.numFrames = null;
        const { imageIds, scaling, dimensions, spacing, origin, direction, dataType, volumeId, referencedVolumeId, metadata, referencedImageIds, additionalDetails, voxelManager, numberOfComponents, reductionStatistic, } = props;
        if (!dataType) {
            throw new Error('Data type is required, please provide a data type as string such as "Uint8Array", "Float32Array", etc.');
        }
        let { imageData } = props;
        this.suppressWarnings = true;
        this.imageIds = imageIds;
        this.volumeId = volumeId;
        this.metadata = metadata;
        this.dimensions = dimensions;
        this.spacing = spacing;
        this.origin = origin;
        this.direction = direction;
        this.dataType = dataType;
        this.reductionStatistic = reductionStatistic;
        this._numberOfComponents = numberOfComponents || 1;
        this.hasPixelSpacing =
            Number.isFinite(spacing[0]) &&
                spacing[0] > 0 &&
                Number.isFinite(spacing[1]) &&
                spacing[1] > 0;
        this.voxelManager =
            voxelManager ??
                VoxelManager.createImageVolumeVoxelManager({
                    dimensions,
                    imageIds,
                    numberOfComponents,
                    id: volumeId,
                    dataType,
                });
        this.numVoxels =
            this.dimensions[0] * this.dimensions[1] * this.dimensions[2];
        if (!imageData) {
            imageData = vtkImageData.newInstance();
            imageData.setDimensions(dimensions);
            imageData.setSpacing(spacing);
            imageData.setDirection(direction);
            imageData.setOrigin(origin);
        }
        imageData.set({
            dataType: dataType,
            voxelManager: this.voxelManager,
            id: volumeId,
            numberOfComponents: numberOfComponents || 1,
        }, this.suppressWarnings);
        imageData.set({
            hasScalarVolume: false,
        }, this.suppressWarnings);
        this.imageData = imageData;
        this.numFrames = this._getNumFrames();
        this._reprocessImageIds();
        if (scaling) {
            this.scaling = scaling;
        }
        if (referencedVolumeId) {
            this.referencedVolumeId = referencedVolumeId;
        }
        if (referencedImageIds) {
            this.referencedImageIds = referencedImageIds;
        }
        if (additionalDetails) {
            this.additionalDetails = additionalDetails;
        }
    }
    get sizeInBytes() {
        return this.voxelManager.sizeInBytes;
    }
    get voxelGrid() {
        return {
            origin: [...this.origin],
            direction: [...this.direction],
            spacing: [...this.spacing],
            dimensions: [...this.dimensions],
        };
    }
    deliveredRegions() {
        return undefined;
    }
    get reductionStatistic() {
        return this._reductionStatistic;
    }
    set reductionStatistic(statistic) {
        if (statistic === this._reductionStatistic) {
            return;
        }
        const effective = statistic ?? VoxelStatistics.Average;
        const reducedWithAnother = this._compositeVoxelManager
            ?.getRepresentations()
            .some((representation) => representation.derivedFrom && representation.statistic !== effective) ||
            this.textureSets.some((set) => set.name !== FULL_RESOLUTION_TEXTURE_SET &&
                set.statistic !== effective);
        if (reducedWithAnother) {
            log.warn(`reductionStatistic of ${this.volumeId} changed to ${statistic} after a reduction with another statistic; existing reduced textures and representations do not match it.`);
        }
        this._reductionStatistic = statistic;
    }
    resetCompositeVoxelManager() {
        this._compositeVoxelManager?.dispose();
        this._compositeVoxelManager = undefined;
    }
    get compositeVoxelManager() {
        const primary = this.voxelManager;
        if (!this._compositeVoxelManager ||
            this._compositeVoxelManager.primary !== primary) {
            this._compositeVoxelManager?.dispose();
            const delivered = this.deliveredRegions();
            this._compositeVoxelManager = new CompositeVoxelManager({
                primary,
                grid: this.voxelGrid,
                delivered,
                quality: delivered === undefined
                    ? ImageQualityStatus.FULL_RESOLUTION
                    : undefined,
                id: `composite-${this.volumeId}`,
                createStorage: (request) => this.createRepresentationStorage(request),
                onDerivedRegionChanged: (region) => {
                    for (const set of this.textureSets) {
                        set.markDirty(region, markSlice);
                    }
                },
            });
        }
        return this._compositeVoxelManager;
    }
    createRepresentationStorage({ grid, statistic, reduction, source, }) {
        if (!voxelGridsEqual(source.grid, this.voxelGrid)) {
            return undefined;
        }
        return provideReducedImages({
            grid,
            sourceImageIds: this.imageIds,
            factors: normalizedFactors(reduction.factors),
            sourceOffset: reduction.sourceOffset,
            statistic,
            dataType: this.dataType,
            numberOfComponents: this._numberOfComponents,
            sharedCacheKey: this.volumeId,
            frameOfReferenceUID: this.metadata?.FrameOfReferenceUID,
            id: `${this.volumeId}-${voxelGridKey(grid, statistic)}`,
        });
    }
    getVoxelRepresentations() {
        return this.compositeVoxelManager.getRepresentations();
    }
    selectVoxelRepresentation(selector = {}) {
        return this.compositeVoxelManager.selectRepresentation(selector);
    }
    createVoxelRepresentation(options) {
        return this.compositeVoxelManager.createRepresentation(options);
    }
    getVoxelQuality(selector = {}) {
        return this.compositeVoxelManager.getQuality(selector);
    }
    getGridQuality(grid, statistic) {
        return this.compositeVoxelManager.getGridQuality(grid, { statistic });
    }
    static get textureBytesUsed() {
        return volumeTextureStore.bytesUsed;
    }
    static get textureBudget() {
        return volumeTextureStore.budget;
    }
    static setTextureBudget(bytes) {
        volumeTextureStore.setBudget(bytes);
    }
    static clearTextureSets() {
        volumeTextureStore.clear();
    }
    get textureSets() {
        return volumeTextureStore.setsOfVolume(this.volumeId);
    }
    getTextureSet(name) {
        return volumeTextureStore.getSet(this.volumeId, name);
    }
    provisionTextureSet(options) {
        return volumeTextureStore.provision({
            volumeId: this.volumeId,
            volumeGrid: this.voxelGrid,
            dataType: this.dataType,
            numberOfComponents: this._numberOfComponents,
            createTexture: ({ grid, statistic }) => {
                const texture = vtkStreamingOpenGLTexture.newInstance();
                texture.setVolumeId(this.volumeId);
                texture.setGrid(grid);
                texture.setStatistic(statistic);
                return texture;
            },
            destroyTexture: (texture) => {
                texture.releaseGraphicsResources();
                texture.delete();
            },
            applyGrid: (texture, grid) => {
                texture.setGrid(grid);
                texture.markAllFramesUpdated();
            },
            markTextureSlice: markSlice,
            ...options,
        });
    }
    claimTextureSet(name) {
        return volumeTextureStore.claim(this.volumeId, name);
    }
    pinTextureSet(name) {
        return volumeTextureStore.pin(this.volumeId, name);
    }
    releaseTextureSet(name) {
        return volumeTextureStore.release(this.volumeId, name);
    }
    getFullResolutionTexture() {
        if (this.vtkOpenGLTexture) {
            return this.vtkOpenGLTexture;
        }
        this.provisionTextureSet({
            name: FULL_RESOLUTION_TEXTURE_SET,
            grids: [this.voxelGrid],
            coverage: 'full-extent',
            backstop: true,
        });
        return this.vtkOpenGLTexture;
    }
    get vtkOpenGLTexture() {
        const member = this.getTextureSet(FULL_RESOLUTION_TEXTURE_SET)?.members()[0];
        return member && !isMutableSlab(member) ? member.texture : undefined;
    }
    markFrameTexturesDirty(frameIndex) {
        this.voxelManager?.invalidateSlice?.(frameIndex);
        for (const set of this.textureSets) {
            set.markDirty(boundsOfFrame(this.voxelGrid, frameIndex), markSlice);
        }
    }
    recordFrameDelivery(frameIndex, quality) {
        this.voxelManager?.invalidateSlice?.(frameIndex);
        this.compositeVoxelManager.acceptData({
            grid: this.voxelGrid,
            frameIndex,
            quality,
        });
    }
    markFrameModified(frameIndex) {
        this.markFrameTexturesDirty(frameIndex);
        this.compositeVoxelManager.refreshDerivedFrames(boundsOfFrame(this.voxelGrid, frameIndex), this.voxelGrid);
    }
    markAllTexturesDirty() {
        for (const set of this.textureSets) {
            set.markAllDirty(markSlice);
        }
    }
    getImageQuality(_imageIdIndex) {
        return ImageQualityStatus.FULL_RESOLUTION;
    }
    get imageIds() {
        return this._imageIds;
    }
    set imageIds(newImageIds) {
        this._imageIds = newImageIds;
        this._reprocessImageIds();
    }
    _reprocessImageIds() {
        this._imageIdsIndexMap.clear();
        this._imageURIsIndexMap.clear();
        this._imageIds.forEach((imageId, i) => {
            const imageURI = imageIdToURI(imageId);
            this._imageIdsIndexMap.set(imageId, i);
            this._imageURIsIndexMap.set(imageURI, i);
        });
    }
    isDynamicVolume() {
        if (this.numTimePoints) {
            return this.numTimePoints > 1;
        }
        return false;
    }
    getImageIdIndex(imageId) {
        return this._imageIdsIndexMap.get(imageId);
    }
    getImageIdByIndex(imageIdIndex) {
        return this._imageIds[imageIdIndex];
    }
    getImageURIIndex(imageURI) {
        return this._imageURIsIndexMap.get(imageURI);
    }
    load(callback) {
    }
    destroy() {
        this.imageData.delete();
        this.imageData = null;
        this.voxelManager.clear();
        this.resetCompositeVoxelManager();
        volumeTextureStore.evictVolume(this.volumeId);
    }
    invalidate() {
        this.markAllTexturesDirty();
        this.imageData.modified();
    }
    modified() {
        this.imageData.modified();
        this._compositeVoxelManager?.rederiveAll();
        this.markAllTexturesDirty();
        this.numFrames = this._getNumFrames();
    }
    removeFromCache() {
        cache.removeVolumeLoadObject(this.volumeId);
    }
    getScalarDataLength() {
        return this.voxelManager.getScalarDataLength();
    }
    _getNumFrames() {
        if (!this.isDynamicVolume()) {
            return this.imageIds.length;
        }
        return this.numTimePoints;
    }
    imageIdIndexToFrameIndex(imageIdIndex) {
        return imageIdIndex % this.numFrames;
    }
    getCornerstoneImages() {
        const { imageIds } = this;
        return imageIds.map((imageId) => {
            return cache.getImage(imageId);
        });
    }
}
export default ImageVolume;
